/**
 * Filters applied before an error reaches Sentry/GlitchTip.
 *
 * Pure functions with no Sentry import on purpose: the handler that uses them
 * lives inside App.svelte's `import.meta.env.MODE === 'production'` block, which
 * no test can enter. Keeping the decisions here makes them testable.
 *
 * cc3-pplb.5 / cc3-pplb.6.
 */

/**
 * URL schemes that are never our code. Browser extensions inject scripts into
 * the page, and their exceptions arrive on our `window`. Safari masks the real
 * URL as `webkit-masked-url://hidden/` (GlitchTip DORIS-FINNLAND-CC3-2IA:
 * "Cannot destructure property 'tabId'", culprit setupExtension).
 *
 * Anchored at the scheme. A substring match on "extension" would also swallow
 * real errors from a bundle chunk that happens to be named that way.
 */
export const THIRD_PARTY_URL_PATTERNS = [
  /^webkit-masked-url:/,
  /^chrome-extension:/,
  /^moz-extension:/,
  /^safari-extension:/,
  /^safari-web-extension:/,
]

const URL_IN_STACK = /[a-z][a-z0-9+.-]*:\/\/[^\s)]+/i

export function isThirdPartyUrl(url) {
  if (typeof url !== 'string' || url === '') return false
  return THIRD_PARTY_URL_PATTERNS.some((pattern) => pattern.test(url))
}

/**
 * True when the error ORIGINATES in third-party code.
 *
 * Deliberately only the topmost frame: an extension that calls into our code
 * and makes it throw is still our bug, and dropping it would hide real
 * failures. Anything unclassifiable is reported — a filter that guesses is
 * worse than noise.
 */
export function isThirdPartyError(event, error) {
  const filename = event && typeof event.filename === 'string' ? event.filename : ''
  if (isThirdPartyUrl(filename)) return true

  const stack = error && typeof error.stack === 'string' ? error.stack : ''
  const match = stack.match(URL_IN_STACK)
  return isThirdPartyUrl(match ? match[0] : '')
}

/**
 * Microsoft Outlook / Defender "Safe Links" opens every link in an incoming
 * mail in a headless CefSharp browser, whose .NET bridge rejects a promise with
 * a bare string like "Object Not Found Matching Id:2, MethodName:update,
 * ParamCount:4" (GlitchTip DORIS 21904/21905). It carries no stack and no
 * extension URL, so isThirdPartyError cannot see it; match it on the message.
 *
 * This is not our code, and it must not send the user to the error page: the
 * rejection on /bulletinboard/... is what bounced the session to
 * /unhandled_error and produced a second event.
 */
export const LINK_SCANNER_REJECTION =
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/

export function isLinkScannerNoise(error) {
  const message =
    typeof error === 'string'
      ? error
      : error && typeof error.message === 'string'
        ? error.message
        : ''
  return LINK_SCANNER_REJECTION.test(message)
}

/**
 * Replace a urql OperationResult with the CombinedError it carries.
 *
 * Capturing the result object itself gives GlitchTip nothing to work with —
 * DORIS-FINNLAND-CC3-2IG is titled "?" and its only content is
 * "Object captured as exception with keys: data, error, extensions, fetching".
 * The actual GraphQL failure is in `.error`.
 *
 * Keyed on `fetching`, which is what distinguishes an OperationResult from any
 * other object that happens to have an `.error` property.
 */
export function unwrapOperationResult(value) {
  if (value && typeof value === 'object' && 'fetching' in value && value.error) {
    return value.error
  }
  return value
}
